/* eslint-disable @typescript-eslint/no-explicit-any */
// templateMessageSendSlice.ts
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { RootState } from '../store';

import axios from 'axios';

interface WhatsappContact {
  input: string;
  wa_id: string;
}

interface WhatsappMessage {
  id: string;
  message_status: string;
}

interface WhatsappResponseInfo {
  contacts: WhatsappContact[];
  messages: WhatsappMessage[];
  messaging_product: string;
}

interface SystemUserPayload {
  to: string;
  type: string;
  template: {
    name: string;
    language: {
      code: string;
    };
    components: Array<{
      type: string;
      parameters?: Array<{
        text?: string;
        type?: string;
        payload?: string;
      }>;
      index?: string;
      sub_type?: string;
    }>;
  };
  recipient_type: string;
  messaging_product: string;
}

interface TemplateMessageResponse {
  id: number;
  status: string;
  channel: string;
  success: boolean;
  user_id: string;
  read_time: string | null;
  recipient: string;
  created_at: number;
  message_id: string;
  system_user: string;
  phone_number: number;
  delivered_time: string | null;
  recipient_payload: string;
  system_user_payload: SystemUserPayload;
  whatsapp_response_info: WhatsappResponseInfo;
}

interface SendTemplateMessagePayload {
  template: string;
  variables: Record<string, string>;
  current_recipient: string;
  user_id: string;
  scheduled_time?: number;
}

interface TemplateMessageState {
  isSending: boolean;
  error: string | null;
  lastSentMessage: TemplateMessageResponse | null;
}

const initialState: TemplateMessageState = {
  isSending: false,
  error: null,
  lastSentMessage: null
};

export const sendTemplateMessage = createAsyncThunk<
  TemplateMessageResponse,
  SendTemplateMessagePayload,
  { rejectValue: string }
>(
  'templateMessage/send',
  async (payload, { rejectWithValue, getState }) => {
    try {
      
      const state = getState() as any;
      const token = state.auth.token;
      
      const response = await axios.post('https://api.insightssystem.com/api:uYdlVwac/web/send/template', payload, {
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: token })
        }
      });
      return response.data as TemplateMessageResponse;
    } catch (error) {
      if (error instanceof Error) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue('An unknown error occurred');
    }
  }
);

const templateMessageSendSlice = createSlice({
  name: 'templateMessage',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    resetSendState: (state) => {
      state.isSending = false;
      state.error = null;
      state.lastSentMessage = null;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(sendTemplateMessage.pending, (state) => {
        state.isSending = true;
        state.error = null;
      })
      .addCase(sendTemplateMessage.fulfilled, (state, action) => {
        state.isSending = false;
        state.error = null;
        state.lastSentMessage = action.payload;
      })
      .addCase(sendTemplateMessage.rejected, (state, action) => {
        state.isSending = false;
        state.error = action.payload ?? 'Failed to send template message';
      });
  },
});

// Selectors
export const selectIsSending = (state: RootState) => state.templateMessage.isSending;
export const selectError = (state: RootState) => state.templateMessage.error;
export const selectLastSentMessage = (state: RootState) => state.templateMessage.lastSentMessage;

export const { clearError, resetSendState } = templateMessageSendSlice.actions;
export default templateMessageSendSlice.reducer;